import type { QuoteItem } from '@techgitter/contracts';

type IdFn = () => string;

const defaultNewId: IdFn = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const reindex = (items: QuoteItem[]): QuoteItem[] =>
  items.map((it, idx) => (it.position === idx ? it : { ...it, position: idx }));

/**
 * Merge ≥2 items into the first id in `idsInOrder`. The survivor keeps its
 * name and position; donor options are concatenated in the order their ids
 * appear in `idsInOrder`. `selectedOptionId` falls through: survivor's
 * choice wins; otherwise the first donor with a non-null choice; otherwise
 * null. `markedForInvoice` is OR'd across all sources — opting items into a
 * group should not silently unmark them. No-op when fewer than 2 valid ids.
 */
export const mergeItems = (
  items: QuoteItem[],
  idsInOrder: string[],
  newId: IdFn = defaultNewId,
): QuoteItem[] => {
  void newId;
  const resolved = idsInOrder
    .map((id) => items.find((it) => it.id === id))
    .filter((it): it is QuoteItem => it !== undefined);
  if (resolved.length < 2) return items;

  const survivor = resolved[0];
  const donors = resolved.slice(1);
  const donorIds = new Set(donors.map((d) => d.id));

  const mergedOptions = resolved.flatMap((it) => it.options);
  const mergedSelected =
    survivor.selectedOptionId ??
    donors.find((d) => d.selectedOptionId !== null)?.selectedOptionId ??
    null;
  const mergedMarked = resolved.some((it) => it.markedForInvoice);

  const next: QuoteItem[] = [];
  for (const it of items) {
    if (donorIds.has(it.id)) continue;
    if (it.id === survivor.id) {
      next.push({
        ...survivor,
        selectedOptionId: mergedSelected,
        markedForInvoice: mergedMarked,
        options: mergedOptions,
      });
    } else {
      next.push(it);
    }
  }
  return reindex(next);
};

/**
 * Pull a single option out of a multi-option item and place it in a new
 * sibling item directly after the parent. The new item inherits the
 * parent's `name` and `markedForInvoice`, and auto-selects its only option
 * (matching the `addItemFromInventory` precedent in QuoteEditor). If the
 * parent's `selectedOptionId` pointed at the moved option, it falls back
 * to the first remaining option — divergent from `removeOption`'s
 * fall-back-to-null because the user is reorganising, not deleting.
 * No-op when the parent has fewer than 2 options.
 */
export const extractOptionToItem = (
  items: QuoteItem[],
  itemId: string,
  optionId: string,
  newId: IdFn = defaultNewId,
): QuoteItem[] => {
  const parentIdx = items.findIndex((it) => it.id === itemId);
  if (parentIdx < 0) return items;
  const parent = items[parentIdx];
  if (parent.options.length < 2) return items;

  const option = parent.options.find((o) => o.id === optionId);
  if (!option) return items;

  const remaining = parent.options.filter((o) => o.id !== optionId);
  const updatedParent: QuoteItem = {
    ...parent,
    options: remaining,
    selectedOptionId:
      parent.selectedOptionId === optionId
        ? remaining[0]?.id ?? null
        : parent.selectedOptionId,
  };

  const child: QuoteItem = {
    id: newId(),
    name: parent.name,
    position: parentIdx + 1,
    selectedOptionId: option.id,
    markedForInvoice: parent.markedForInvoice,
    options: [option],
  };

  const next = [...items];
  next.splice(parentIdx, 1, updatedParent);
  next.splice(parentIdx + 1, 0, child);
  return reindex(next);
};
